/**
 * WebAuthn/passkey (Прил. B, Phase 2.1 — обе рельсы).
 *   • Enroll / status / disable — rail-neutral `/api/v1/me/webauthn/*` (MeWebauthnController).
 *   • Passkey-login — rail-aware: vitrine → `/v1/showcase/auth/webauthn/login/*` (by email),
 *     full → `/v1/auth/webauthn/login/*` (by phone). Один WebauthnStepUpService на бэке.
 *   • Reveal step-up — showcase-only (`/v1/showcase/me/webauthn/stepup/*`), PAN/CVV reveal витрины.
 * Фронт-либа — @simplewebauthn/browser (снимает CBOR/base64url/attestation).
 */
import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
  platformAuthenticatorIsAvailable,
} from '@simplewebauthn/browser';
import { http } from './http';
import type { Product } from '@/product';
import type { StepUpPurpose } from './stepup';

/**
 * Passkey-login endpoints + identifier field by product (pre-auth, so the caller — the login screen —
 * passes the product explicitly): vitrine → showcase endpoints, email identifier; full → auth
 * endpoints, phone identifier.
 */
function loginOptions(identifier: string, product: Product) {
  return product === 'vitrine'
    ? http.post('/v1/showcase/auth/webauthn/login/options', { email: identifier })
    : http.post('/v1/auth/webauthn/login/options', { phoneE164: identifier });
}
function loginVerify(asResp: unknown, product: Product) {
  return product === 'vitrine'
    ? http.post('/v1/showcase/auth/webauthn/login/verify', asResp)
    : http.post('/v1/auth/webauthn/login/verify', asResp);
}

// Passkey заводится на КОНКРЕТНОМ устройстве (device-bound, синк между устройствами не подразумеваем).
// Флаг «на этом устройстве заведён» — локальный (localStorage per-browser), чтобы решать, показывать
// ли предложение включить биометрию после magic-link входа.
const DEVICE_KEY = 'vitrine-passkey-device';

export function passkeyEnrolledOnDevice(): boolean {
  try { return localStorage.getItem(DEVICE_KEY) === 'on'; } catch { return false; }
}
function markEnrolledOnDevice() {
  try { localStorage.setItem(DEVICE_KEY, 'on'); } catch { /* private mode — ignore */ }
}
/**
 * Снять ТОЛЬКО локальную отметку «passkey заведён здесь», не трогая серверные ключи. Self-heal: флаг мог
 * остаться от прошлой регистрации, ключ которой уже удалён из системы/браузера — тогда WebAuthn-церемония
 * находит 0 ключей и Chrome показывает «Ключи доступа не найдены». Зовём при провале церемонии, чтобы
 * перестать предлагать Face ID на этом устройстве до повторной регистрации.
 */
export function forgetPasskeyDevice() {
  try { localStorage.removeItem(DEVICE_KEY); } catch { /* private mode — ignore */ }
}

/** true если браузер + платформенный аутентификатор (FaceID/Touch ID/fingerprint) доступны. */
export async function passkeySupported(): Promise<boolean> {
  try {
    return browserSupportsWebAuthn() && (await platformAuthenticatorIsAvailable());
  } catch {
    return false;
  }
}

/** Завести passkey на этом устройстве (биометрия). Бросает при отмене/ошибке. */
export async function registerPasskey(deviceLabel?: string): Promise<void> {
  const options = await http.post('/v1/me/webauthn/register/options').then(r => r.data);
  try {
    const attResp = await startRegistration({ optionsJSON: options });
    await http.post('/v1/me/webauthn/register/verify', { ...attResp, deviceLabel });
  } catch (e: any) {
    // InvalidStateError = passkey уже заведён на этом аутентификаторе (excludeCredentials match).
    // Это не ошибка — просто «уже включено», помечаем устройство и идём дальше без красного.
    if (e?.name === 'InvalidStateError') { markEnrolledOnDevice(); return; }
    throw e;
  }
  markEnrolledOnDevice();
}

/**
 * Заведён ли passkey НА ЭТОМ УСТРОЙСТВЕ (server-truth, per-device по tacita_device cookie, V083).
 * Клиентский UI обычно опирается на быстрый локальный флаг {@link passkeyEnrolledOnDevice}; это —
 * авторитетная серверная проверка того же (для сверки/будущего использования).
 */
export async function passkeyStatus(): Promise<boolean> {
  try {
    const r = await http.get('/v1/me/webauthn/status').then(x => x.data);
    return !!r.enabled;
  } catch {
    return false;
  }
}

/**
 * Выключить биометрию НА ЭТОМ УСТРОЙСТВЕ: DELETE резолвит tacita_device cookie и удаляет только ключ(и)
 * этого устройства (per-device, V083) — passkey на других устройствах клиента не трогаются — и снимает
 * локальную отметку. Для явных действий (тумблер off / logout). Self-heal при провале церемонии — это
 * {@link forgetPasskeyDevice} (локально, без серверного удаления).
 */
export async function disablePasskey(): Promise<void> {
  await http.delete('/v1/me/webauthn');
  try { localStorage.removeItem(DEVICE_KEY); } catch { /* private mode — ignore */ }
}

/**
 * Вход по passkey (биометрия) — identifier → options → assertion → токены. identifier = email на
 * vitrine, phone на full; product выбирает рельс. Бросает при отмене/ошибке.
 */
export async function loginWithPasskey(identifier: string, product: Product) {
  const options = await loginOptions(identifier, product).then(r => r.data);
  const asResp = await startAuthentication({ optionsJSON: options });
  return loginVerify(asResp, product).then(r => r.data);
}

/**
 * Biometric step-up → одноразовый grant (передаётся в X-Reveal-Grant). {@code purpose} scopes the
 * grant to the operation (REVEAL / PIN_CHANGE / CARD_BLOCK / CARD_UNFREEZE / MASTER_KEY_EXPORT) — the backend binds it and rejects cross-use. Бросает
 * если пользователь отменил биометрию.
 */
export async function stepUpForReveal(purpose: StepUpPurpose = 'REVEAL'): Promise<string> {
  const options = await http.post('/v1/showcase/me/webauthn/stepup/options').then(r => r.data);
  const asResp = await startAuthentication({ optionsJSON: options });
  const r = await http
    .post(`/v1/showcase/me/webauthn/stepup/verify?purpose=${purpose}`, asResp)
    .then(r => r.data);
  return r.revealGrant as string;
}
